import React, { useEffect, useState } from "react";
import toast, { Toaster } from 'react-hot-toast';
import {
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import axios from "axios";
import PageHeader1 from "../../components/common/PageHeader1";

const WalletReport = () => {
  // --- COMPONENT STATE ---
  const [walletDataList, setWalletDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationDetail, setPaginationDetail] = useState({});

  // State for the main page filter
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // State for export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // --- DATA FETCHING & EFFECTS ---
  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }

    fetchWalletData();
  }, [paginationModel, appliedSearch]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;

      const params = { page, limit };

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/walletBalanceReport`,
        {
          params: params,
          headers: { Authorization: token },
        }
      );

      // Handle different response formats
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      if (data.length > 0 || response.data) {
        setWalletDataList(data.length > 0 ? data : (Array.isArray(response.data) ? response.data : []));
        setPaginationDetail({
          totalRecords: response.data?.paginationDetail?.totalRecords ||
            response.data?.totalRecords ||
            response.data?.total ||
            (data.length > 0 ? data.length : 0),
        });
      }
    } catch (error) {
      console.error("Error fetching wallet report:", error);
      setError(error.response?.data?.message || "Failed to fetch wallet report");
      toast.error(error.response?.data?.message || "Failed to fetch wallet report");
    } finally {
      setLoading(false);
    }
  };

  // Show toast for fetch errors
  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`);
    }
  }, [error]);

  // --- EVENT HANDLERS ---
  const handleSearch = () => {
    if (!searchValue.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      fetchWalletData();
      return;
    }
    setAppliedSearch(searchValue);
    // Call API with only search parameter (no page/limit)
    const token = localStorage.getItem("vedhvidhi-admin-token");
    setLoading(true);
    axios.get(
      `${process.env.REACT_APP_BASEURL}/admin/walletBalanceReport`,
      {
        params: { search: searchValue.trim() },
        headers: { Authorization: token },
      }
    ).then(response => {
      // Handle different response formats
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      if (data.length > 0 || response.data) {
        setWalletDataList(data.length > 0 ? data : (Array.isArray(response.data) ? response.data : []));
        setPaginationDetail({
          totalRecords: data.length || response.data?.data?.length || 0,
        });
      }
    }).catch(error => {
      console.error("Error searching:", error);
      toast.error(error.response?.data?.message || "Failed to search");
    }).finally(() => {
      setLoading(false);
    });
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      fetchWalletData();
    }
  };

  // Reset filters and fetch all data
  const handleReset = () => {
    setSearchValue("");
    setAppliedSearch("");
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchWalletData();
    toast.success("Filters reset. Showing all data.");
  };

  // --- DATA & COLUMNS for DataGrid ---
  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  const rowCount = appliedSearch
    ? (walletDataList?.length || 0)
    : (paginationDetail?.totalRecords || walletDataList?.length || 0);

  // The 'rows' variable is ALWAYS derived from the state, so it reflects the latest filtered data.
  const rows = walletDataList.map((item, index) => ({
    ...item,
    id: item._id || item.id || index,
    sono: (paginationModel.page * paginationModel.pageSize) + index + 1,
    username: item.username || "N/A",
    mobileNumber: item.mobileNumber || "N/A",
    giftAmount: item.giftAmount || 0,
    totalBalance: item.totalBalance || 0,
    actualBalance: item.actualBalance || 0,
    updatedAt: item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : "N/A",
  }));

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "username", headerName: "Username", width: 150 },
    { field: "mobileNumber", headerName: "Mobile Number", width: 150 },
    { field: "totalBalance", headerName: "Total Balance", width: 130, type: "number" },
    { field: "giftAmount", headerName: "Gift Amount", width: 130, type: "number" },
    { field: "actualBalance", headerName: "Actual Balance", width: 130, type: "number" },
    { field: "updatedAt", headerName: "Updated At", width: 180 },
  ];

  /**
   * EXPORT LOGIC:
   * Fetches all data from API without pagination and exports to CSV file.
   */
  const handleExportSubmit = async () => {
    setIsExporting(true);

    try {
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const params = {};

      // Add search parameter if search is applied
      if (appliedSearch) {
        params.search = appliedSearch;
      }

      // Fetch all data without pagination
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/walletBalanceReport`,
        {
          params: params,
          headers: { Authorization: token },
        }
      );

      // Handle different response formats
      let exportData = [];
      if (Array.isArray(response.data)) {
        exportData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        exportData = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        exportData = response.data.data;
      }

      if (exportData.length === 0) {
        toast.error('No data available to export');
        setExportDialogOpen(false);
        setIsExporting(false);
        return;
      }

      // Helper to properly format data for CSV (handles commas, quotes)
      const escapeCsvCell = (cell) => {
        if (cell === null || cell === undefined) return '';
        const cellString = String(cell);
        if (cellString.includes(',') || cellString.includes('"') || cellString.includes('\n')) {
          return `"${cellString.replace(/"/g, '""')}"`;
        }
        return cellString;
      };

      // Map data to rows format
      const mappedData = exportData.map((item, index) => ({
        id: item._id || item.id || index,
        sono: index + 1,
        username: item.username || "N/A",
        mobileNumber: item.mobileNumber || "N/A",
        giftAmount: item.giftAmount || 0,
        totalBalance: item.totalBalance || 0,
        actualBalance: item.actualBalance || 0,
        updatedAt: item?.updatedAt ? new Date(item.updatedAt).toLocaleString() : "N/A",
      }));

      // 1. Get the columns to export
      const exportableColumns = columns;

      // 2. Create the header row from column definitions
      const headerRow = exportableColumns.map(col => escapeCsvCell(col.headerName)).join(',');

      // 3. Create data rows from the fetched data
      const dataRows = mappedData.map(row =>
        exportableColumns.map(col =>
          escapeCsvCell(row[col.field])
        ).join(',')
      );

      // 4. Combine header and data into a single CSV string
      const csvContent = [headerRow, ...dataRows].join('\n');

      // 5. Create a Blob and trigger the browser download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      // Create filename
      const fileName = appliedSearch
        ? `wallet-report-${appliedSearch}.csv`
        : 'wallet-report.csv';
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Export successful! Check your downloads.');
      setExportDialogOpen(false); // Close the confirmation dialog
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.message || `Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <Toaster />
        <PageHeader1 pagetitle="Wallet Report" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search Section - Inside table card, single line */}
                  <Box display="flex" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    <TextField
                      placeholder="Search..."
                      value={searchValue}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      disabled={loading || !searchValue.trim()}
                      sx={{
                        height: '40px',
                        width: '40px',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '&:disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled',
                        }
                      }}
                    >
                      <FaSearch />
                    </IconButton>
                    <Button variant="contained" color="success" onClick={() => setExportDialogOpen(true)} sx={{ height: '40px' }}>
                      Export
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleReset}
                      sx={{ height: '40px' }}
                    >
                      Clear
                    </Button>
                  </Box>

                  <Box sx={{ height: 600, width: "100%" }}>
                    <DataGrid
                      rows={rows}
                      columns={columns}
                      loading={loading}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50, 100]}
                      paginationMode={appliedSearch ? "client" : "server"}
                      disableRowSelectionOnClick
                      getRowId={(row) => row.id}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- DIALOGS --- */}
      {/* Export Dialog - just a confirmation */}
      <Dialog open={exportDialogOpen} onClose={() => !isExporting && setExportDialogOpen(false)}>
        <DialogTitle>Export Wallet Report</DialogTitle>
        <DialogContent>
          <Typography>
            This will export the currently displayed data as a CSV file.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} disabled={isExporting}>Cancel</Button>
          <Button onClick={handleExportSubmit} variant="contained" disabled={isExporting}>
            {isExporting ? "Exporting..." : "Confirm & Export"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WalletReport;

